import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth';
import { SeasonCountdownBanner } from './SeasonCountdownBanner';
import { QuestPanel } from './QuestPanel';
import { StreakPanel } from './StreakPanel';
import { AnimatedHubButton } from './AnimatedHubButton';
import './FloatingHub.css';

type PanelKey = 'season' | 'quests' | 'streak' | null;

export function FloatingHub() {
  const { token } = useAuth();
  const [open, setOpen] = useState<PanelKey>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <div className="hub-container">
        <AnimatedHubButton
          emoji="🏆"
          onPress={() => open === 'season' ? setOpen(null) : setOpen('season')}
          btnStyle="hub-btn-season"
          delay={1200}
        />
        {token && (
          <AnimatedHubButton
            emoji="🔥"
            onPress={() => open === 'streak' ? setOpen(null) : setOpen('streak')}
            btnStyle="hub-btn-streak"
            delay={600}
          />
        )}
        {token && (
          <AnimatedHubButton
            emoji="📜"
            onPress={() => open === 'quests' ? setOpen(null) : setOpen('quests')}
            btnStyle="hub-btn-quests"
            delay={0}
          />
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="hub-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
          >
            <div className="hub-fx-layer">
              <span className="hub-glow-ring r1" />
              <span className="hub-glow-ring r2" />
              <span className="hub-glow-ring r3" />
              {Array.from({ length: 8 }).map((_, i) => {
                const angle = (i / 8) * Math.PI * 2;
                const dist = 160;
                const sx = Math.cos(angle) * dist;
                const sy = Math.sin(angle) * dist;
                return (
                  <span
                    key={i}
                    className={`hub-spark${i % 2 === 0 ? '' : ' alt'}`}
                    style={{ ['--sx' as string]: `${sx}px`, ['--sy' as string]: `${sy}px` }}
                  />
                );
              })}
            </div>
            <motion.div
              className="hub-popup"
              initial={{ 
                scale: 0.2, 
                rotate: -15,
                x: 160,
                y: 160,
                opacity: 0 
              }}
              animate={{ 
                scale: 1, 
                rotate: 0,
                x: 0,
                y: 0,
                opacity: 1 
              }}
              exit={{ 
                scale: 0.3, 
                rotate: 10,
                x: 120,
                y: 120,
                opacity: 0 
              }}
              transition={{
                type: 'spring',
                stiffness: 90,
                damping: 7,
              }}
              onClick={e => e.stopPropagation()}
            >
              <button className="hub-close" onClick={() => setOpen(null)} aria-label="Kapat">×</button>
              {open === 'season' && <SeasonCountdownBanner />}
              {open === 'streak' && <StreakPanel />}
              {open === 'quests' && <QuestPanel />}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
